// Shared validation for a persisted storage-object path (BL-1455).
//
// Since BL-1455 the database stores a storage PATH rather than an absolute
// URL, and the servable address is built per response. That moves a persisted
// string onto the front of a URL, so the string's shape is now load-bearing:
// a value carrying a scheme, a protocol-relative prefix, or `..` traversal
// stops being a key inside our bucket and starts being something else.
//
// One helper for every persisted storage path. Its consumers, by package:
// core (inventory media and external documents), core-admin (the admin
// request schemas), admin-services (lot documents) and core-bidder (public
// auctions). It
// exists because two copies were written apart and immediately drifted: the
// lienholder proof field (deleted since, BL-2185) got the guard and the media
// field, whose blast radius is WIDER (its URL is served to the anonymous bidder
// site, not fetched behind an authenticated signed-URL proxy), did not. Sharing
// the refinement is what stops that recurring.
//
// Pure + client-safe: no `server-only`, no I/O.
import { z } from '../../zod';

/** Max length of a persisted storage key — bounds the JSONB/column payload. */
export const MAX_STORAGE_PATH_LENGTH = 500;

/**
 * Is this a plain relative storage key — no scheme, no protocol-relative
 * prefix, no absolute root, no directory traversal?
 *
 * Exported so a caller can apply the same test outside Zod (a route guard, a
 * migration backfill) without re-deriving the rules.
 *
 * @param value the candidate storage path
 * @returns true when the value is a safe relative key
 */
export function isSafeStoragePath(value: string): boolean {
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return false; // any URL scheme
  if (value.startsWith('//')) return false; // protocol-relative
  if (value.startsWith('/')) return false; // escapes the bucket root
  if (value.split('/').includes('..')) return false; // traversal
  return true;
}

/**
 * Is this path inside the given tenant's own namespace?
 *
 * Every path `generateStoragePath` mints is `<tenantId>/<entity>/…`, so the
 * leading segment IS the owning tenant. The presign + upload-complete routes
 * already enforce this at UPLOAD time; this is the same check at PERSIST time,
 * for the fields that store a caller-supplied path.
 *
 * Without it, an operator with legitimate PATCH access to their own record can
 * point it at an object under a DIFFERENT tenant's prefix — the tenant check
 * passes (they do own the record), and the value is a well-formed relative key,
 * so nothing else objects. Both affected buckets are public-exposure, so this
 * is cross-tenant asset *reference* rather than a confidentiality breach — but
 * it lets one tenant display another's branding, and it is a step toward worse
 * if anything in those buckets ever stops being public.
 *
 * @param value    the candidate storage path
 * @param tenantId the tenant the record belongs to
 * @returns true when the path's leading segment is this tenant
 */
export function isPathOwnedByTenant(value: string, tenantId: string): boolean {
  return value.startsWith(`${tenantId}/`);
}

/**
 * Does every record in this list keep its storage path inside the tenant's
 * namespace? The array form of {@link isPathOwnedByTenant}, for the fields that
 * persist a caller-supplied list of paths: a lot's whole `media` list on the lot
 * save (BUG-2024). A writer of one record (BL-2338's per-item media routes)
 * calls {@link isPathOwnedByTenant} directly.
 *
 * A record with no `storagePath` passes: it carries no path to own, and the
 * write-side schema already decides whether a path is required.
 *
 * A video's `posterStoragePath` (BL-2439) is a second persisted path on the same
 * record, so it is checked too: every caller covers both by calling this once.
 *
 * @param items    the records about to be persisted, each with an optional path
 * @param tenantId the tenant the owning record belongs to
 * @returns true when no record's path sits outside this tenant's prefix
 */
export function areStoragePathsOwnedByTenant(
  items: ReadonlyArray<{ storagePath?: string | null; posterStoragePath?: string | null }>,
  tenantId: string,
): boolean {
  const owned = (path: string | null | undefined) => path == null || isPathOwnedByTenant(path, tenantId);
  return items.every((item) => owned(item.storagePath) && owned(item.posterStoragePath));
}

/**
 * Zod schema for a persisted storage-object path.
 *
 * Deliberately emits one message per distinct failure rather than a single
 * "invalid path": the four rules fail for different reasons and an operator
 * (or a test) benefits from knowing which one fired.
 *
 * @param label human-readable field name, used to prefix each message
 * @returns a Zod string schema enforcing the safe-relative-key rules
 */
export function storagePathSchema(label: string) {
  return z
    .string()
    .max(MAX_STORAGE_PATH_LENGTH, `${label} is too long`)
    .refine((p) => !/^[a-z][a-z0-9+.-]*:/i.test(p), `${label} must not carry a URL scheme`)
    .refine((p) => !p.startsWith('//'), `${label} must not be protocol-relative`)
    .refine((p) => !p.startsWith('/'), `${label} must be relative to the bucket`)
    .refine((p) => !p.split('/').includes('..'), `${label} must not traverse directories`);
}
